<template>
  <JobInventoryAndQuantityPicker
    :model-value="mappedFormData"
    :resource-type-activity-id="resourceTypeActivityId"
    :is-disabled="disabled"
    :show-arrow="false"
    :hint="description"
    :inventory-sub-type-error-messages="subTypeErrorMessages"
    :quantity-error-messages="quantityErrorMessages"
    :required="false"
    @update:modelValue="onUpdate($event)"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import JobInventoryAndQuantityPicker from '@/components/tickets/StopsAndInventories/InventoryPickers/JobInventoryAndQuantityPicker.vue'
import type { InventoryPickerItem } from '@/components/tickets/StopsAndInventories/InventoryPickers/types'
import { v4 } from 'uuid'
import { useInventoryTypesWithResourceTypeActivity } from '@/composables/inventory-types/useInventoryTypes'
const INVENTORY_SUB_TYPE_KEY = 'inventory_sub_type_id'
const QUANTITY_KEY = 'quantity'
const RESOURCE_TYPE_FILTER_ID_KEY = 'resource_type_activity_id'

const emit = defineEmits<{
  (
    event: 'change',
    value: {
      [INVENTORY_SUB_TYPE_KEY]?: number | null
      [QUANTITY_KEY]?: number | null
    },
  )
}>()

const props = withDefaults(
  defineProps<{
    description?: string | null
    errorSchema?: {
      [INVENTORY_SUB_TYPE_KEY]?: { errors?: string[] }
      [QUANTITY_KEY]?: { errors?: string[] }
      errors?: string[]
    }
    formData: unknown
    businessUnitId: string
    params: unknown
    rootSchema: unknown
    schema: unknown
    title: string | null
    uiSchema: unknown
    disabled?: boolean
  }>(),
  {
    disabled: false,
    errorSchema: () => {
      return {}
    },
    description: null,
  },
)

// Error messages
const subTypeErrorMessages = computed<string[]>(() => {
  return (props.errorSchema?.errors || props.errorSchema?.[INVENTORY_SUB_TYPE_KEY]?.errors) ?? []
})
const quantityErrorMessages = computed<string[]>(() => {
  return props.errorSchema?.errors != null || props.errorSchema?.[QUANTITY_KEY]?.errors != null
    ? ['Required']
    : []
})

const mappedFormData = computed<InventoryPickerItem>(() => {
  return {
    inventorySubTypeId: props.formData?.[INVENTORY_SUB_TYPE_KEY] ?? null,
    quantity: props.formData?.[QUANTITY_KEY] ?? null,
    _uniqueId: v4(),
    id: null,
  }
})
const onUpdate = (event: InventoryPickerItem) => {
  const eventData = {}
  eventData[INVENTORY_SUB_TYPE_KEY] = event.inventorySubTypeId
  eventData[QUANTITY_KEY] = event.quantity ?? null

  emit('change', eventData)
}

const resourceTypeActivityId = computed<number | null>(
  () => props.schema?.[RESOURCE_TYPE_FILTER_ID_KEY] ?? null,
)
const { isFetching, allInventoryTypes, filteredInventoryTypesByResourceTypeActivityId } =
  useInventoryTypesWithResourceTypeActivity(resourceTypeActivityId)

const reloadSelector = ref(0)

watch(
  filteredInventoryTypesByResourceTypeActivityId,
  (newInventoryOptions) => {
    if (
      !isFetching.value &&
      (allInventoryTypes.value?.length ?? 0) > 0 &&
      props.formData?.[INVENTORY_SUB_TYPE_KEY] &&
      !newInventoryOptions?.some((i) =>
        i.inventorySubTypes?.some((c) => c.id === props.formData?.[INVENTORY_SUB_TYPE_KEY]),
      )
    ) {
      onUpdate({
        quantity: mappedFormData.value.quantity ?? null,
        inventorySubTypeId: null,
        _uniqueId: v4(),
        id: null,
      })
      reloadSelector.value += 1 // ensures the selector popup is not showing inventory options that are no longer valid
    }
  },
  { immediate: true },
)
</script>
